/**
 * The browser-local result cache.
 *
 * After a query returns, the first N rows are materialised into a DuckDB-WASM
 * table and every subsequent sort, filter, page and column statistic is
 * generated as SQL against *that* table -- zero network per interaction
 * (spec §4.7). MotherDuck's stated rationale: 100 ms is the perceptual
 * "instant" threshold and a client->cloud round trip is 70-100 ms, which
 * consumes the entire budget.
 *
 * When the result exceeds N rows we fall back to server round trips. The mode
 * is always reported to the UI -- silent degradation is worse than slow.
 */

import type { ColumnSchema, DuckDbType, ResultRow } from '@/types/api';
import {
  DEFAULT_MAX_CACHE_ROWS,
  type CacheStatus,
  type GridMode,
  type LocalEngine,
  type LocalQueryResult,
  type RawResult,
  type RemoteExecutor,
  type ViewResult,
  type ViewSpec,
} from './types';
import { buildCountSql, buildViewSql, localTableName, remoteSource } from './sqlgen';
import { profileColumn, type ColumnProfile } from './stats';
import { distinctValuesSql } from '@/components/filter/filterModel';
import { groupedSchema, isGroupActive, type GroupState } from '@/components/group/groupModel';
import { quoteIdent, renderableText } from '@/lib/values';

/** One entry in the filter panel's Suggested Values list. */
export interface DistinctValue {
  readonly value: string | null;
  readonly isNull: boolean;
  readonly count: number;
}

/**
 * How long the in-browser engine gets to boot before we give up on it.
 *
 * This exists because a dead Web Worker does not reject -- `instantiate()`
 * simply never settles. Without a deadline the grid sits on "materializing"
 * forever, which is the one outcome worse than degrading to the server.
 */
export const ENGINE_BOOT_TIMEOUT_MS = 20_000;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (cause: unknown) => {
        clearTimeout(timer);
        reject(cause instanceof Error ? cause : new Error(String(cause)));
      },
    );
  });
}

export interface ResultCacheOptions {
  readonly maxRows?: number;
  /** Resolves the WASM engine lazily; rejecting flips the cache to `unavailable`. */
  readonly engineFactory: () => Promise<LocalEngine>;
  readonly remote?: RemoteExecutor;
  readonly onStatusChange?: (status: CacheStatus) => void;
  /** Overridable for tests. */
  readonly bootTimeoutMs?: number;
}

export class ResultCache {
  private readonly maxRows: number;
  private readonly engineFactory: () => Promise<LocalEngine>;
  private readonly remote: RemoteExecutor | null;
  private readonly onStatusChange: ((status: CacheStatus) => void) | null;
  private readonly bootTimeoutMs: number;

  private engine: LocalEngine | null = null;
  private enginePromise: Promise<LocalEngine> | null = null;

  private mode: GridMode = 'idle';
  private reason: string | null = null;
  private result: RawResult | null = null;
  private tableName: string | null = null;
  private types: Map<string, DuckDbType> = new Map();
  private profiles = new Map<string, ColumnProfile>();

  constructor(options: ResultCacheOptions) {
    this.maxRows = options.maxRows ?? DEFAULT_MAX_CACHE_ROWS;
    this.engineFactory = options.engineFactory;
    this.remote = options.remote ?? null;
    this.onStatusChange = options.onStatusChange ?? null;
    this.bootTimeoutMs = options.bootTimeoutMs ?? ENGINE_BOOT_TIMEOUT_MS;
  }

  getStatus(): CacheStatus {
    return {
      mode: this.mode,
      cachedRows: this.mode === 'local' ? (this.result?.rows.length ?? 0) : 0,
      reason: this.reason,
      lastError: null,
    };
  }

  getColumns(): readonly ColumnSchema[] {
    return this.result?.columns ?? [];
  }

  private setMode(mode: GridMode, reason: string | null): void {
    this.mode = mode;
    this.reason = reason;
    this.onStatusChange?.(this.getStatus());
  }

  private async getEngine(): Promise<LocalEngine> {
    if (this.engine) return this.engine;
    if (!this.enginePromise) {
      this.enginePromise = withTimeout(
        this.engineFactory(),
        this.bootTimeoutMs,
        `In-browser DuckDB did not start within ${Math.round(this.bootTimeoutMs / 1000)}s`,
      );
    }
    try {
      this.engine = await this.enginePromise;
      return this.engine;
    } catch (cause) {
      this.enginePromise = null;
      throw cause;
    }
  }

  /**
   * Loads a fresh query result. Resolves to the mode the grid will operate in.
   */
  async load(result: RawResult): Promise<GridMode> {
    this.profiles.clear();
    this.result = result;
    this.types = new Map(result.columns.map((column) => [column.name, column.type]));

    const tooLarge = result.truncated || result.rows.length > this.maxRows;

    if (tooLarge) {
      await this.dropCurrentTable();
      this.setMode(
        'remote',
        `Result exceeds the ${this.maxRows.toLocaleString()}-row local cache limit. ` +
          'Sorting and filtering run on the server.',
      );
      return this.mode;
    }

    this.setMode('materializing', null);

    try {
      const engine = await this.getEngine();
      const table = localTableName(result.queryId);
      await engine.materialize(table, result.columns, result.rows);

      // Verify the cache actually holds what we put in it. A short or empty
      // materialisation would otherwise render as a perfectly ordinary "0 rows"
      // -- indistinguishable from a genuinely empty result, and the worst kind
      // of silent data loss. Failing here degrades to server mode instead.
      const check = await engine.query(
        `SELECT count(*) AS n FROM ${quoteIdent(table)}`,
      );
      const cached = Number(check.rows[0]?.[0] ?? 0);
      if (cached !== result.rows.length) {
        await engine.drop(table).catch(() => undefined);
        throw new Error(
          `cache holds ${cached} of ${result.rows.length} rows after materialising ` +
            `${result.columns.length} columns`,
        );
      }

      this.tableName = table;
      this.setMode('local', null);
    } catch (cause) {
      this.tableName = null;
      const message = cause instanceof Error ? cause.message : String(cause);

      // Full detail (bundle, worker URL, isolation state) goes to the console;
      // the strip gets the short actionable version.
      console.error('[duckdb-cache] local engine unavailable:', message, cause);

      // Everything before the diagnostics blob, which is appended after ". bundle=".
      const summary = message.split(/\.\s*bundle=/)[0] ?? message;
      this.setMode(
        'unavailable',
        `Local cache unavailable: ${summary}. Sorting and filtering run on the server.`,
      );
    }

    return this.mode;
  }

  private async dropCurrentTable(): Promise<void> {
    if (this.tableName && this.engine) {
      try {
        await this.engine.drop(this.tableName);
      } catch {
        /* A stale cache table is harmless; never fail a load over it. */
      }
    }
    this.tableName = null;
  }

  /** True when the local table can answer view queries. */
  isLocal(): boolean {
    return this.mode === 'local' && this.tableName !== null && this.engine !== null;
  }

  /**
   * Answers a grid view. Runs locally when cached, otherwise re-issues the
   * user's query to the server with the sort/filter applied.
   */
  async view(spec: ViewSpec, signal?: AbortSignal): Promise<ViewResult> {
    const started = performance.now();
    const result = this.result;
    if (!result) {
      return { rows: [], totalRows: 0, mode: 'local', elapsedMs: 0 };
    }
    const grouped = Boolean(spec.group && isGroupActive(spec.group));

    if (this.isLocal()) {
      const engine = this.engine as LocalEngine;
      const source = `"${(this.tableName as string).replace(/"/g, '""')}"`;
      const options = { source, types: this.types };

      const [page, count] = await Promise.all([
        engine.query(buildViewSql(spec, options)),
        engine.query(buildCountSql(spec.filters, options, spec.predicate, spec.group)),
      ]);

      const totalRows = Number(count.rows[0]?.[0] ?? 0);
      return {
        rows: page.rows,
        totalRows: Number.isFinite(totalRows) ? totalRows : result.rows.length,
        mode: 'local',
        elapsedMs: performance.now() - started,
        columns: grouped ? groupedSchema(spec.group as GroupState, this.types) : null,
      };
    }

    // The rows are already in memory. If the user has not asked for a sort or a
    // filter, paging through them needs no engine and no network -- this is the
    // common case when WASM failed to start but the result itself was small.
    const untouched =
      spec.sort.length === 0 &&
      spec.filters.length === 0 &&
      (spec.predicate ?? '').trim() === '' &&
      !grouped;
    if (!this.remote || (untouched && !result.truncated)) {
      const rows = result.rows.slice(spec.offset, spec.offset + spec.limit);
      return {
        rows,
        totalRows: result.rows.length,
        mode: 'remote',
        elapsedMs: performance.now() - started,
      };
    }

    const options = { source: remoteSource(result.sql), types: this.types };
    const page = await this.remote.run(buildViewSql(spec, options), signal);
    const count = await this.remote.run(
      buildCountSql(spec.filters, options, spec.predicate, spec.group),
      signal,
    );
    const totalRows = Number(count.rows[0]?.[0] ?? 0);

    return {
      rows: page.rows,
      totalRows: Number.isFinite(totalRows) ? totalRows : 0,
      mode: 'remote',
      elapsedMs: performance.now() - started,
      columns: grouped ? groupedSchema(spec.group as GroupState, this.types) : null,
    };
  }

  /**
   * Column statistics for the Column Explorer. Cached per column, since the
   * underlying table is immutable for the life of a result.
   */
  async profile(column: ColumnSchema): Promise<ColumnProfile | null> {
    const existing = this.profiles.get(column.name);
    if (existing) return existing;
    if (!this.isLocal()) return null;

    const profile = await profileColumn(
      this.engine as LocalEngine,
      this.tableName as string,
      column,
    );
    this.profiles.set(column.name, profile);
    return profile;
  }

  /**
   * Distinct values for the Suggested Values list in the filter panel.
   *
   * Local-cache only, deliberately. Returns null when there is no cache rather
   * than firing a server query per keystroke, and the panel explains why.
   */
  async distinctValues(column: string, limit = 500): Promise<readonly DistinctValue[] | null> {
    if (!this.isLocal()) return null;
    const engine = this.engine as LocalEngine;
    const result = await engine.query(
      distinctValuesSql(this.tableName as string, column, limit),
    );

    return result.rows.map((row) => {
      const isNull = row[1] === true || row[1] === 1 || row[1] === 1n;
      return {
        value: isNull ? null : renderableText(row[0]),
        isNull,
        count: Number(row[2] ?? 0),
      };
    });
  }

  /**
   * The complete current view, for a client-side export.
   *
   * Not the visible page: exporting only the 500 rows the grid happens to be
   * showing, silently, would be a data-loss bug of exactly the kind this
   * codebase keeps guarding against. Bounded by the cache limit, which is the
   * most the local table can hold anyway.
   */
  async exportView(
    spec: Pick<ViewSpec, 'sort' | 'filters' | 'predicate' | 'group'>,
  ): Promise<LocalQueryResult | null> {
    if (!this.isLocal()) return null;
    const engine = this.engine as LocalEngine;
    const source = `"${(this.tableName as string).replace(/"/g, '""')}"`;
    const sql = buildViewSql(
      { ...spec, offset: 0, limit: this.maxRows },
      { source, types: this.types },
    );
    const result = await engine.query(sql);
    const grouped = Boolean(spec.group && isGroupActive(spec.group));
    return {
      columns: grouped ? groupedSchema(spec.group as GroupState, this.types) : result.columns,
      rows: result.rows,
    };
  }

  /** Materialised rows, for export without a second round trip. */
  getRows(): readonly ResultRow[] {
    return this.result?.rows ?? [];
  }

  /**
   * Parquet bytes for the cached result, written by DuckDB-WASM.
   * Returns null when the result is not cached locally -- the caller then has
   * to ask the server for a `COPY ... TO ... (FORMAT PARQUET)`.
   */
  async exportParquet(): Promise<Uint8Array | null> {
    if (!this.isLocal()) return null;
    const engine = this.engine as LocalEngine;
    if (!engine.exportParquet) return null;
    return engine.exportParquet(this.tableName as string);
  }

  async dispose(): Promise<void> {
    await this.dropCurrentTable();
    if (this.engine) {
      await this.engine.dispose();
      this.engine = null;
      this.enginePromise = null;
    }
    this.result = null;
    this.profiles.clear();
    this.setMode('idle', null);
  }
}
